import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/api/axios-instance";

/**
 * Hand-written calls of the public sign-in endpoints (not in the committed OpenAPI schema). They bypass the
 * interceptors of axiosInstance: a refused sign-in answers 401, which there signs out and reloads the page.
 */
const publicClient = axios.create({ baseURL: axiosInstance.defaults.baseURL });

export interface GoogleSignInProvider {
  client_id: string;
  /** the Workspace domain whose accounts may sign in (algovectra.com) */
  domain: string;
}

/** GET /api/admin/auth/providers: sign-in methods besides the password. */
export interface SignInProviders {
  google: GoogleSignInProvider | null;
}

/** The sign-in methods of the backend; an older backend without the endpoint offers the password only. */
export function useSignInProviders() {
  return useQuery({
    queryKey: ["sign-in-providers"],
    queryFn: async ({ signal }) => {
      const { data } = await publicClient.get<SignInProviders>("/api/admin/auth/providers", { signal });
      return data;
    },
    staleTime: Infinity,
    retry: 1,
  });
}

export interface GoogleSignInResponse {
  token: string;
  login: string;
}

/** Why the backend refused a Google sign-in (`code` of POST /api/admin/auth/google). */
export type GoogleSignInFailure = "wrong_domain" | "account_disabled" | "unavailable" | "failed";

/** POST /api/admin/auth/google with the ID token from Google Identity Services. */
export async function postGoogleCredential(credential: string): Promise<GoogleSignInResponse> {
  const { data } = await publicClient.post<GoogleSignInResponse>("/api/admin/auth/google", { credential });
  return data;
}

/** Why a password sign-in failed: `use_google` = a developer account while Google sign-in is on. */
export type PasswordSignInFailure = "use_google" | "failed";

export function passwordSignInFailure(error: unknown): PasswordSignInFailure {
  return axios.isAxiosError(error) && error.response?.data?.code === "use_google" ? "use_google" : "failed";
}

export function googleSignInFailure(error: unknown): GoogleSignInFailure {
  const code = axios.isAxiosError(error) ? error.response?.data?.code : undefined;
  return code === "wrong_domain" || code === "account_disabled" || code === "unavailable" ? code : "failed";
}
